import { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

const Contact = () => {
  const [generalForm, setGeneralForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  const [candidateForm, setCandidateForm] = useState({
    name: '',
    email: '',
    phone: '',
    position: '',
    experience: '',
    resume: null
  });

  const [employerForm, setEmployerForm] = useState({
    company: '',
    contactName: '',
    email: '',
    phone: '',
    roleType: '',
    message: ''
  });

  const handleGeneralSubmit = (e) => {
    e.preventDefault();
    toast.success('Thank you for contacting us! We\'ll get back to you within 24 hours.');
    setGeneralForm({ name: '', email: '', phone: '', message: '' });
  };

  const handleCandidateSubmit = (e) => {
    e.preventDefault();
    toast.success('Your candidate registration has been submitted successfully!');
    setCandidateForm({ name: '', email: '', phone: '', position: '', experience: '', resume: null });
  };

  const handleEmployerSubmit = (e) => {
    e.preventDefault();
    toast.success('Thank you for your inquiry! Our team will contact you shortly.');
    setEmployerForm({ company: '', contactName: '', email: '', phone: '', roleType: '', message: '' });
  };

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-brand-50 via-white to-brand-50/50">
        <div className="max-w-7xl mx-auto text-center">
          <div className="inline-block mb-6 px-6 py-3 bg-brand-400/10 rounded-full border border-brand-400/20">
            <p className="text-brand-400 font-bold text-lg">📞 We're Here to Help</p>
          </div>
          <h1 className="text-5xl md:text-6xl font-black text-gray-900 mb-8 tracking-tight leading-tight">
            Get In <span className="text-brand-400">Touch</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Whether you're looking for your next career move or seeking the perfect candidate, 
            we're here to help. Let's start the conversation.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <Card className="bg-white border-gray-200 hover:border-brand-400 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-brand-400/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Mail className="text-brand-400" size={28} />
                </div>
                <h3 className="text-gray-900 font-bold mb-3 text-lg">Email Us</h3>
                <a href="mailto:info@growthtalentgroup.com.au" className="text-gray-600 hover:text-brand-400 text-base transition-colors duration-200">
                  info@growthtalentgroup.com.au
                </a>
              </CardContent>
            </Card>
            <Card className="bg-white border-gray-200 hover:border-brand-400 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-brand-400/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Phone className="text-brand-400" size={28} />
                </div>
                <h3 className="text-gray-900 font-bold mb-3 text-lg">Call Us</h3>
                <p className="text-gray-600 text-base font-semibold">1300 GROWTH</p>
              </CardContent>
            </Card>
            <Card className="bg-white border-gray-200 hover:border-brand-400 hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-brand-400/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <MapPin className="text-brand-400" size={28} />
                </div>
                <h3 className="text-gray-900 font-bold mb-3 text-lg">Location</h3>
                <p className="text-gray-600 text-base">Servicing Australia Wide</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Forms */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <Tabs defaultValue="general" className="w-full">
            <TabsList className="grid w-full grid-cols-3 bg-white mb-8">
              <TabsTrigger value="general" className="data-[state=active]:bg-brand-400 data-[state=active]:text-black">
                General Inquiry
              </TabsTrigger>
              <TabsTrigger value="candidate" className="data-[state=active]:bg-brand-400 data-[state=active]:text-black">
                Candidate Registration
              </TabsTrigger>
              <TabsTrigger value="employer" className="data-[state=active]:bg-brand-400 data-[state=active]:text-black">
                Employer Inquiry
              </TabsTrigger>
            </TabsList>

            {/* General Contact Form */}
            <TabsContent value="general">
              <Card className="bg-white border-gray-200">
                <CardContent className="p-8">
                  <form onSubmit={handleGeneralSubmit} className="space-y-6">
                    <div>
                      <Label htmlFor="general-name" className="text-gray-900">Name *</Label>
                      <Input
                        id="general-name"
                        type="text"
                        placeholder="Your full name"
                        value={generalForm.name}
                        onChange={(e) => setGeneralForm({ ...generalForm, name: e.target.value })}
                        required
                        className="bg-white border-gray-300 text-gray-900 mt-2"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="general-email" className="text-gray-900">Email *</Label>
                        <Input
                          id="general-email"
                          type="email"
                          placeholder="your@email.com"
                          value={generalForm.email}
                          onChange={(e) => setGeneralForm({ ...generalForm, email: e.target.value })}
                          required
                          className="bg-white border-gray-300 text-gray-900 mt-2"
                        />
                      </div>
                      <div>
                        <Label htmlFor="general-phone" className="text-gray-900">Phone</Label>
                        <Input
                          id="general-phone"
                          type="tel"
                          placeholder="Your phone number"
                          value={generalForm.phone}
                          onChange={(e) => setGeneralForm({ ...generalForm, phone: e.target.value })}
                          className="bg-white border-gray-300 text-gray-900 mt-2"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="general-message" className="text-gray-900">Message *</Label>
                      <Textarea
                        id="general-message"
                        placeholder="How can we help you?"
                        value={generalForm.message}
                        onChange={(e) => setGeneralForm({ ...generalForm, message: e.target.value })}
                        required
                        rows={6}
                        className="bg-white border-gray-300 text-gray-900 mt-2"
                      />
                    </div>
                    <Button type="submit" className="bg-brand-400 hover:bg-brand-500 text-white font-bold w-full py-7 text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
                      Send Message
                      <Send className="ml-2" size={18} />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Candidate Registration Form */}
            <TabsContent value="candidate">
              <Card className="bg-white border-gray-200">
                <CardContent className="p-8">
                  <p className="text-gray-600 mb-6">
                    Register with us to access exclusive job opportunities in property and strata management.
                  </p>
                  <form onSubmit={handleCandidateSubmit} className="space-y-6">
                    <div>
                      <Label htmlFor="candidate-name" className="text-gray-900">Full Name *</Label>
                      <Input
                        id="candidate-name"
                        type="text"
                        placeholder="Your full name"
                        value={candidateForm.name}
                        onChange={(e) => setCandidateForm({ ...candidateForm, name: e.target.value })}
                        required
                        className="bg-white border-gray-300 text-gray-900 mt-2"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="candidate-email" className="text-gray-900">Email *</Label>
                        <Input
                          id="candidate-email"
                          type="email"
                          placeholder="your@email.com"
                          value={candidateForm.email}
                          onChange={(e) => setCandidateForm({ ...candidateForm, email: e.target.value })}
                          required
                          className="bg-white border-gray-300 text-gray-900 mt-2"
                        />
                      </div>
                      <div>
                        <Label htmlFor="candidate-phone" className="text-gray-900">Phone *</Label>
                        <Input
                          id="candidate-phone"
                          type="tel"
                          placeholder="Your phone number"
                          value={candidateForm.phone}
                          onChange={(e) => setCandidateForm({ ...candidateForm, phone: e.target.value })}
                          required
                          className="bg-white border-gray-300 text-gray-900 mt-2"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="candidate-position" className="text-gray-900">Desired Position *</Label>
                      <Input
                        id="candidate-position"
                        type="text"
                        placeholder="e.g., Property Manager, Strata Manager"
                        value={candidateForm.position}
                        onChange={(e) => setCandidateForm({ ...candidateForm, position: e.target.value })}
                        required
                        className="bg-white border-gray-300 text-gray-900 mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="candidate-experience" className="text-gray-900">Years of Experience *</Label>
                      <Input
                        id="candidate-experience"
                        type="text"
                        placeholder="e.g., 5 years"
                        value={candidateForm.experience}
                        onChange={(e) => setCandidateForm({ ...candidateForm, experience: e.target.value })}
                        required
                        className="bg-white border-gray-300 text-gray-900 mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="candidate-resume" className="text-gray-900">Upload Resume</Label>
                      <Input
                        id="candidate-resume"
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={(e) => setCandidateForm({ ...candidateForm, resume: e.target.files[0] })}
                        className="bg-white border-gray-300 text-gray-900 mt-2"
                      />
                      <p className="text-gray-500 text-xs mt-1">PDF, DOC, or DOCX (Max 5MB)</p>
                    </div>
                    <Button type="submit" className="bg-brand-400 hover:bg-brand-500 text-white font-bold w-full py-7 text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
                      Register as Candidate
                      <Send className="ml-2" size={18} />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Employer Inquiry Form */}
            <TabsContent value="employer">
              <Card className="bg-white border-gray-200">
                <CardContent className="p-8">
                  <p className="text-gray-600 mb-6">
                    Looking to hire top talent? Let us help you find the perfect candidates for your organisation.
                  </p>
                  <form onSubmit={handleEmployerSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="employer-company" className="text-gray-900">Company Name *</Label>
                        <Input
                          id="employer-company"
                          type="text"
                          placeholder="Your company name"
                          value={employerForm.company}
                          onChange={(e) => setEmployerForm({ ...employerForm, company: e.target.value })}
                          required
                          className="bg-white border-gray-300 text-gray-900 mt-2"
                        />
                      </div>
                      <div>
                        <Label htmlFor="employer-contact" className="text-gray-900">Contact Name *</Label>
                        <Input
                          id="employer-contact"
                          type="text"
                          placeholder="Your full name"
                          value={employerForm.contactName}
                          onChange={(e) => setEmployerForm({ ...employerForm, contactName: e.target.value })}
                          required
                          className="bg-white border-gray-300 text-gray-900 mt-2"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <Label htmlFor="employer-email" className="text-gray-900">Email *</Label>
                        <Input
                          id="employer-email"
                          type="email"
                          placeholder="company@email.com"
                          value={employerForm.email}
                          onChange={(e) => setEmployerForm({ ...employerForm, email: e.target.value })}
                          required
                          className="bg-white border-gray-300 text-gray-900 mt-2"
                        />
                      </div>
                      <div>
                        <Label htmlFor="employer-phone" className="text-gray-900">Phone *</Label>
                        <Input
                          id="employer-phone"
                          type="tel"
                          placeholder="Company phone number"
                          value={employerForm.phone}
                          onChange={(e) => setEmployerForm({ ...employerForm, phone: e.target.value })}
                          required
                          className="bg-white border-gray-300 text-gray-900 mt-2"
                        />
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="employer-role" className="text-gray-900">Role Type *</Label>
                      <Input
                        id="employer-role"
                        type="text"
                        placeholder="e.g., Senior Property Manager, Strata Coordinator"
                        value={employerForm.roleType}
                        onChange={(e) => setEmployerForm({ ...employerForm, roleType: e.target.value })}
                        required
                        className="bg-white border-gray-300 text-gray-900 mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="employer-message" className="text-gray-900">Additional Details</Label>
                      <Textarea
                        id="employer-message"
                        placeholder="Tell us about your hiring needs..."
                        value={employerForm.message}
                        onChange={(e) => setEmployerForm({ ...employerForm, message: e.target.value })}
                        rows={6}
                        className="bg-white border-gray-300 text-gray-900 mt-2"
                      />
                    </div>
                    <Button type="submit" className="bg-brand-400 hover:bg-brand-500 text-white font-bold w-full py-7 text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105">
                      Submit Inquiry
                      <Send className="ml-2" size={18} />
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </section>
    </div>
  );
};

export default Contact;
